import {
  defaultDataIdFromObject,
  InMemoryCache,
  type PossibleTypesMap,
  type TypePolicies,
} from "@apollo/client/cache"
import {
  auditFacts,
  auditLogEvent,
  dataSource,
  employee,
  entityParty,
  firmClient,
  layout,
  objectAcl,
  objectMapping,
  queries,
  reportPackage,
  reportPackageDocument,
  searchResult,
  team,
  teamMember,
  threads,
  transaction,
  transactionParty,
  transactionsWithSummaries,
  vaultFields,
  workItem,
} from "src/shared/initializers/typePolicies"
import { auditCapture } from "src/shared/initializers/typePolicies/auditCapture"
import { department } from "src/shared/initializers/typePolicies/department"

const typePolicies: TypePolicies = {
  AuditLogReportDisplay: { keyFields: false },
  Query: {
    fields: {
      ...queries,
      auditCaptureSources: { merge: false },
      auditCaptureBundles: { merge: false },
      auditCaptureBundleTransactions: { merge: false },
      auditCaptureTransaction: { merge: false },
    },
  },
  ...objectAcl,
  ...teamMember,
  ...team,
  ...employee,
  ...firmClient,
  ...auditFacts,
  ...auditCapture,
  ...auditLogEvent,
  ...transaction,
  ...transactionParty,
  ...transactionsWithSummaries,
  ...threads,
  ...searchResult,
  ...entityParty,
  ...reportPackage,
  ...reportPackageDocument,
  ...layout,
  ...workItem,
  ...dataSource,
  ...vaultFields,
  ...objectMapping,
  ...department,
}

// A fragment written on a union type matches a member only when the union is listed here. Without
// the entry the cache writes the member's __typename and drops its fields.
const possibleTypes: PossibleTypesMap = {
  MetricTerm: ["MetricCategoriesOperand", "MetricSummaryTypeOperand", "MetricOperation"],
  AuditLogSummaryAssertion: [
    "AuditLogDocumentTypeChanged",
    "AuditLogReconciliationCreatedByUser",
    "AuditLogReconciliationDeletedByUser",
    "AuditLogFlagResolved",
    "AuditLogFlagsSurfacedSummary",
    "AuditLogCategoryCreated",
    "AuditLogCategoryEdited",
    "AuditLogJournalEntryImportRequested",
    "AuditLogJournalEntryImportCompleted",
    "AuditLogJournalEntryImportFailed",
    "AuditLogReportPublishedAsDraftSummary",
    "AuditLogReportPublishedAsFinalSummary",
  ],
  AuditLogAssertion: [
    "AuditLogFlagResolved",
    "AuditLogFlagsSurfaced",
    "AuditLogCategoryCreated",
    "AuditLogReconciliationCreatedByUser",
    "AuditLogReconciliationDeletedByUser",
    "AuditLogCategoryEdited",
    "AuditLogDocumentTypeChanged",
    "AuditLogReportPublishedAsDraft",
    "AuditLogReportPublishedAsFinal",
    "AuditLogJournalEntryImportRequested",
    "AuditLogJournalEntryImportCompleted",
    "AuditLogJournalEntryImportFailed",
  ],
  AuditLogDisplay: [
    "AuditLogTransactionDisplay",
    "AuditLogCategoryDisplay",
    "AuditLogReconciliationDisplay",
    "AuditLogReportDisplay",
  ],
}

export const cache = new InMemoryCache({
  typePolicies,
  possibleTypes,
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case "InvoiceDiscount":
        return `InvoiceDiscount:${object.invoiceDiscountId}`
      case "BankAccount":
        return `BankAccount:${object.id}:${object.isDefault}:${object.isAutoPayout}`
      case "Check":
        return `Check:${object.id}:${object.isDefault}:${object.isAutoPayout}`
      // Turn off caching for specific types
      // Category is included here to address a hierarchy rendering issue.
      case "Category":
      case "CategoryTransactionSummary":
      case "QuickBooksIntegration":
      case "User":
      case "InvoicingProfile":
      case "Customer":
      case "CustomerSummary":
      case "CustomerAddress":
      case "InvoicingAddress":
      case "Discount":
      case "VendorSummary":
      case "ScheduleEditedObject":
      case "ScheduleSnapshot":
        return undefined
      default:
        return defaultDataIdFromObject(object)
    }
  },
})
